"use client";

import { c as _c } from "react/compiler-runtime";
import { useInfiniteQuery } from "@tanstack/react-query";
import { useCallback, useEffect, useMemo, useRef } from "react";
import { FiImage, FiUpload } from "react-icons/fi";
import { HiOutlinePhotograph } from "react-icons/hi";
import { LuLoader2 } from "react-icons/lu";
import Image from "next/image";
import { Button } from "@/components/ui";
import { Dialog, DialogBody, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { isImageUrl } from "@/lib/image-compression";
import { cx } from "@/lib/tailwind-utils";
import { AttachmentDayPage } from "@/types/notes";
import { noteQueryOptions } from "@/api/provider/scribe/note-query";
interface GhostReferencePickerProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  patientId?: string;
  // Called with a gallery photo's (pre-signed) URL.
  onSelectUrl: (url: string) => void;
  // Called with a file the user uploaded from their device.
  onSelectFile: (file: File) => void;
}

// Single-select chooser for a reference photo to ghost/align against: the
// patient's existing gallery photos (same data as the comparison gallery) plus
// an upload-from-device option.
export default function GhostReferencePicker(t0) {
  const $ = _c(47);
  const {
    open,
    onOpenChange,
    patientId,
    onSelectUrl,
    onSelectFile
  } = t0;
  const uploadInputRef = useRef(null);
  const sentinelRef = useRef(null);
  const observerRef = useRef(null);
  let t1;
  if ($[0] !== patientId) {
    t1 = noteQueryOptions.attachmentsInfinite(patientId);
    $[0] = patientId;
    $[1] = t1;
  } else {
    t1 = $[1];
  }
  const queryOptions = t1;
  const t2 = open && !!patientId;
  let t3;
  if ($[2] !== queryOptions || $[3] !== t2) {
    t3 = {
      ...queryOptions,
      enabled: t2,
      initialPageParam: undefined as string | undefined,
      getNextPageParam: _temp
    };
    $[2] = queryOptions;
    $[3] = t2;
    $[4] = t3;
  } else {
    t3 = $[4];
  }
  const {
    data,
    isLoading,
    hasNextPage,
    fetchNextPage,
    isFetchingNextPage
  } = useInfiniteQuery(t3);
  let t4;
  let t5;
  if ($[5] !== fetchNextPage || $[6] !== hasNextPage || $[7] !== isFetchingNextPage || $[8] !== open) {
    t4 = () => {
      if (open && hasNextPage && !isFetchingNextPage) {
        fetchNextPage();
      }
    };
    t5 = [open, hasNextPage, isFetchingNextPage, fetchNextPage];
    $[5] = fetchNextPage;
    $[6] = hasNextPage;
    $[7] = isFetchingNextPage;
    $[8] = open;
    $[9] = t4;
    $[10] = t5;
  } else {
    t4 = $[9];
    t5 = $[10];
  }
  useEffect(t4, t5);
  let t6;
  if ($[11] !== fetchNextPage || $[12] !== hasNextPage || $[13] !== isFetchingNextPage) {
    t6 = node => {
      if (isFetchingNextPage) {
        return;
      }
      if (observerRef.current) {
        observerRef.current.disconnect();
      }
      observerRef.current = new IntersectionObserver(entries => {
        if (entries[0].isIntersecting && hasNextPage) {
          fetchNextPage();
        }
      });
      if (node) {
        observerRef.current.observe(node);
      }
      sentinelRef.current = node;
    };
    $[11] = fetchNextPage;
    $[12] = hasNextPage;
    $[13] = isFetchingNextPage;
    $[14] = t6;
  } else {
    t6 = $[14];
  }
  const setObserver = t6;
  data?.pages;
  let t7;
  if ($[15] !== data?.pages) {
    const pages = data?.pages?.filter(Boolean) as AttachmentDayPage[] ?? [];
    t7 = pages.flatMap(_temp2).filter(_temp3).map(_temp4);
    $[15] = data?.pages;
    $[16] = t7;
  } else {
    t7 = $[16];
  }
  const images = t7;
  let t8;
  if ($[17] !== onOpenChange || $[18] !== onSelectUrl) {
    t8 = url => {
      if (!url) {
        return;
      }
      onSelectUrl(url);
      onOpenChange(false);
    };
    $[17] = onOpenChange;
    $[18] = onSelectUrl;
    $[19] = t8;
  } else {
    t8 = $[19];
  }
  const handlePick = t8;
  let t9;
  if ($[20] !== onOpenChange || $[21] !== onSelectFile) {
    t9 = event => {
      const file = event.target.files?.[0];
      event.target.value = "";
      if (!file || !file.type.startsWith("image/")) {
        return;
      }
      onSelectFile(file);
      onOpenChange(false);
    };
    $[20] = onOpenChange;
    $[21] = onSelectFile;
    $[22] = t9;
  } else {
    t9 = $[22];
  }
  const handleUploadChange = t9;
  let t10;
  if ($[23] === Symbol.for("react.memo_cache_sentinel")) {
    t10 = <DialogHeader><DialogTitle>Choose a comparison photo</DialogTitle><DialogDescription>Pick a photo from the patient's gallery to line the shot up with, or upload one.</DialogDescription></DialogHeader>;
    $[23] = t10;
  } else {
    t10 = $[23];
  }
  let t11;
  if ($[24] !== handlePick || $[25] !== hasNextPage || $[26] !== images || $[27] !== isFetchingNextPage || $[28] !== isLoading || $[29] !== patientId || $[30] !== setObserver) {
    t11 = <DialogBody className="max-h-[60vh] overflow-y-auto">{patientId && isLoading && images.length === 0 ? <div className="flex items-center justify-center p-8"><LuLoader2 className="h-6 w-6 animate-spin text-foreground-muted" /></div> : images.length === 0 ? <div className="flex flex-col items-center justify-center p-8 text-center"><HiOutlinePhotograph className="mb-3 h-12 w-12 text-foreground-muted" /><p className="text-body text-muted-foreground">{patientId ? "No photos in this patient's gallery yet" : "No gallery available here"}</p><p className="mt-1 text-caption text-foreground-muted">Upload a photo to compare against.</p></div> : <div className="grid grid-cols-4 gap-3">{images.map(image => <button key={image.id} type="button" onClick={() => handlePick(image.url)} className={cx("relative aspect-square overflow-hidden rounded-lg border-2 border-transparent bg-muted transition-all", "hover:border-primary focus:border-primary focus:outline-none")}>{image.thumbnailUrl || image.url ? <Image src={image.thumbnailUrl || image.url || ""} alt={image.name || "Gallery photo"} fill={true} className="object-cover" unoptimized={true} /> : <div className="flex h-full w-full items-center justify-center"><FiImage className="h-8 w-8 text-foreground-muted" /></div>}</button>)}{hasNextPage && <div ref={setObserver} className="col-span-4 py-4 text-center text-caption text-foreground-muted">{isFetchingNextPage ? <LuLoader2 className="mx-auto h-4 w-4 animate-spin" /> : "Scroll to load more"}</div>}</div>}</DialogBody>;
    $[24] = handlePick;
    $[25] = hasNextPage;
    $[26] = images;
    $[27] = isFetchingNextPage;
    $[28] = isLoading;
    $[29] = patientId;
    $[30] = setObserver;
    $[31] = t11;
  } else {
    t11 = $[31];
  }
  let t12;
  if ($[32] !== handleUploadChange) {
    t12 = <input ref={uploadInputRef} type="file" accept="image/*" onChange={handleUploadChange} className="hidden" />;
    $[32] = handleUploadChange;
    $[33] = t12;
  } else {
    t12 = $[33];
  }
  let t13;
  if ($[34] !== onOpenChange) {
    t13 = <Button variant="secondary" onClick={() => onOpenChange(false)}>Cancel</Button>;
    $[34] = onOpenChange;
    $[35] = t13;
  } else {
    t13 = $[35];
  }
  let t14;
  if ($[36] === Symbol.for("react.memo_cache_sentinel")) {
    t14 = <Button onClick={() => uploadInputRef.current?.click()}><FiUpload className="mr-2 h-4 w-4" />Upload from device</Button>;
    $[36] = t14;
  } else {
    t14 = $[36];
  }
  let t15;
  if ($[37] !== t12 || $[38] !== t13) {
    t15 = <DialogFooter>{t12}{t13}{t14}</DialogFooter>;
    $[37] = t12;
    $[38] = t13;
    $[39] = t15;
  } else {
    t15 = $[39];
  }
  let t16;
  if ($[40] !== t11 || $[41] !== t15) {
    t16 = <DialogContent className="max-w-3xl">{t10}{t11}{t15}</DialogContent>;
    $[40] = t11;
    $[41] = t15;
    $[42] = t16;
  } else {
    t16 = $[42];
  }
  let t17;
  if ($[43] !== onOpenChange || $[44] !== open || $[45] !== t16) {
    t17 = <Dialog open={open} onOpenChange={onOpenChange}>{t16}</Dialog>;
    $[43] = onOpenChange;
    $[44] = open;
    $[45] = t16;
    $[46] = t17;
  } else {
    t17 = $[46];
  }
  return t17;
}
function _temp4(a_0) {
  return {
    id: a_0.id,
    url: a_0.signedUrl,
    thumbnailUrl: a_0.thumbnailSignedUrl,
    name: a_0.name
  };
}
function _temp3(a) {
  return !a.isPlaceholder && isImageUrl(a.gcsPath || a.signedUrl || a.name || "");
}
function _temp2(page) {
  return page.data;
}
function _temp(lastPage) {
  return lastPage?.nextDate ?? undefined;
}